import { computed, Injectable, signal, WritableSignal } from "@angular/core";
import { LeadService, Student } from "../../lib";
import { MsalService } from "@azure/msal-angular";
import { Observable, Subscription } from "rxjs";
import { GenesysCloudService } from '../genesys-cloud.service';
import { Models } from "purecloud-platform-client-v2";
import { AdmissionUICommonDataService } from "../admission-ui/admission-ui-common-data.service";
import { AdmissionUIMessengerService } from "../admission-ui/admission-ui-messenger.service";
import { AppInsightsService } from "../app-insights.service";
import { HttpClient } from "@angular/common/http";
import { AppConfigService } from "../app-config.service";

export interface LeadConversationState {
    Student?: Student;
    DialerContactId?: string;
    TelecomDialerCampaignName? : string;
    Call?: Models.CallConversation;
    Email?: Models.EmailConversation;
    SubscriptionInterval?: number;
    CallStatus?: string;
    PhoneNumber?: string;
    CallType?: string;
    WaitingOnDisposition?: boolean;
    LastActivePageName?: string;
    CallConversationId?: string;
    CallStartTimestamp?: number;
    DispositionStartTimestamp?: number;
    PreviousUserStatus?: string;
    PSTSMSDisabled?: boolean;
    PSTEmailDisabled?: boolean;
    StitchInWindow?: boolean;
}

@Injectable({
    providedIn: 'root' // Updated to make this a platform injector
})
export class LeadConversationStateStore {
    conversationId: string | null = null;
    callType: string | null = null;
    dialerContactId: string | null = null;
    genesysUserId = localStorage.getItem('genesysUserId') ?? 'NOT SET';
    isLeadUserInterface = (localStorage.getItem('isLeadUserInterface') ?? "") === 'true';
    appInsightsProperties: Record<string, any> = {};
    emailTopicSubscription: Subscription | undefined;
    canClose: boolean = false;
    callTopicSubscription: Subscription | undefined;
    userPresenceSubscription: Subscription | undefined;
    dispositionInterval!: ReturnType<typeof setInterval>
    private readonly _state = signal<LeadConversationState>({});
    private lastProcessedConversationId: string | null = null; // Track the last processed conversation ID
    private validProviders = ['Skynet', 'Lion'];

    constructor(private readonly leadService: LeadService,
        private readonly msalService: MsalService,
        private readonly genesysCloudService: GenesysCloudService,
        private readonly messenger: AdmissionUIMessengerService,
        private readonly commonDataService: AdmissionUICommonDataService,
        private readonly appInsightsService: AppInsightsService,
        private readonly http: HttpClient,
        private readonly configurationService: AppConfigService) {
        window.addEventListener('storage', (event) => {
            if (event.key === 'isLeadUserInterface') {
                this.isLeadUserInterface = (localStorage.getItem('isLeadUserInterface') ?? "") === 'true';
            }
            if (event.key === 'genesysUserId') {
                this.genesysUserId = localStorage.getItem('genesysUserId') ?? 'NOT SET';
            }
            this.emailSubscription();
        });
        this.emailSubscription();
        this.callSubscription();
        this.userPresencceSubscription();

        // Subscribe to waitingOnDisposition$ from AdmissionUIMessengerService
        this.messenger.monitorWaitingOnDispositionData().subscribe((waiting) => {
            this._state.update(state => ({ ...state, WaitingOnDisposition: waiting }));
        });

        // Subscribe to canClose$ from AdmissionUIMessengerService
        this.messenger.canClose$.subscribe((canClose) => {
            this.canClose = canClose;
            if (this.canClose) {
                this.messenger.clearLocalStorageState();
                window.close();
            }
        });

        if (typeof window !== 'undefined' && window.opener) {
            window.onbeforeunload = (event) => {
                if (!this.canClose) {
                    event.preventDefault();
                }
                else {
                    this.clearState(); // clear local storage on the modal domain 
                }
            };
        }
        if (!localStorage.getItem('interaction_state')) {
            // clear any connected email conversations before opening the lead UI
            this.genesysCloudService.getEmailConversations().subscribe(email => {
                email?.entities?.forEach((element: Models.EmailConversation) => {
                    let agentParticipant = this.findLastInParticipants(element.participants!, (participant: Models.EmailMediaParticipant) =>
                        participant.user?.id !== undefined && participant.user.id === this.genesysUserId);
                    if (agentParticipant && agentParticipant.state === 'connected') {                        
                        let customerParticipant = this.findLastInParticipants(element.participants!, (participant: Models.EmailMediaParticipant) =>
                            participant.purpose === 'customer');
                        if(this.validProviders.includes(customerParticipant.provider)) {
                            this.genesysCloudService.declineEmailConversation(element.id!, agentParticipant.id);
                        }
                    }
                    if (agentParticipant.state === 'alerting') {
                        this.handleAdmissionsEvents(element);
                    }
                });
            });
        }
        this.commonDataService.isStitchIn.subscribe(isStitchIn => {
            this._state.update(state => ({ ...state, StitchInWindow: isStitchIn }));
        });
    }

    public endInteraction(phoneNumber: string, conversationId?: string, clearDialpadState: boolean = true): void {
        if (phoneNumber === this._state().PhoneNumber || conversationId === this._state().Email?.id) {
            this.messenger.setPage('Idle');
            this.clearState();
            this.messenger.clearLocalStorageState();
            this.messenger.setWaitingOnDispositionCrossDomain(false);
            if (clearDialpadState) {
                this.messenger.setSetDialpadInitStateCrossDomain();
            }
        }
    }

    public clearState() {
        this.messenger.setClearPreviousEmailConversationId(true);
        this.messenger.clearLocalStorageState(); // Clear the localStorage interaction state
        this.commonDataService.setIsStitchIn(false);
        this.lastProcessedConversationId = null; // Reset the last processed conversation ID
        this.conversationId = null;
        this._state.set({});
        clearInterval(this.dispositionInterval);
        this._state.update(state => ({ ...state, WaitingOnDisposition: false }));
    }

    callSubscription() {
        let callTopic = 'conversations.calls';
        this.callTopicSubscription = this.genesysCloudService.subscribeToTopic(callTopic).subscribe(notification => {
            this.handleCallEvents(notification.eventBody)
        });
    }

    userPresencceSubscription() {
        let userRoutingStatusTopic = 'presence';
        this.userPresenceSubscription = this.genesysCloudService.subscribeToTopic(userRoutingStatusTopic).subscribe(notification => {
            this.handleUserPresenceStatus(notification.eventBody)
        });
    }

    handleCallEvents(conversation: any) {
        if (!this._state().Call || this._state().CallConversationId == conversation.id) {
            let agentParticipant = this.findLastInParticipants(conversation.participants, (participant) =>
                participant.user?.id !== undefined && participant.user.id === this.genesysUserId);
            let associatedEmail = agentParticipant?.attributes?.['leadFlow_Associated_EmailConversationId'];
            if(this.isCorrectCallId(associatedEmail)) {
                this.updateCallConversation(conversation);
                if (this.isUpdateDispositionTimeStampNeeded(agentParticipant)) {
                    this._state.update(state => ({ ...state, DispositionStartTimestamp: Date.now() }));
                }
                if (agentParticipant.state === 'connected') {
                    this._state.update(state => ({ ...state, CallStartTimestamp: new Date(agentParticipant.connectedTime).getTime(), CallStatus: agentParticipant.state }));
                }
                this.updateCallStatus(agentParticipant.disconnectType === "transfer" ? "transferred" : agentParticipant.state);
            }else{
                this.appInsightsService.logTrace('IgnoringCallConversationUpdate_inconsistentEmail', {
                                    conversationId: conversation.id,
                                    associatedEmailId: associatedEmail,
                                    emailId: this._state().Email?.id
                                    });
                console.log('Ignoring call conversation update as associated email does not match. Associated Email:', associatedEmail, 'Email Conversation ID in state:', this._state().Email?.id);
            }
        }
    }

    isCorrectCallId(associatedEmail: any): boolean {
        return !associatedEmail || !this._state().Email?.id || this._state().Email?.id === associatedEmail
    }

    isUpdateDispositionTimeStampNeeded(agentParticipant: any): boolean {
        return this._state().CallStatus !== agentParticipant.state && (this.commonDataService.terminatedStatus.includes(agentParticipant.state) && !this._state().DispositionStartTimestamp)
    }

    emailSubscription(): void {
        if (this.isLeadUserInterface) {
            this.appInsightsProperties['User'] = this.msalService.instance.getActiveAccount()?.name
            this.appInsightsProperties['Admissions'] = 'true'

            let emailTopic = 'conversations.emails';

            this.commonDataService.getUIConfigurationAndSchoolStatuses().subscribe(({ uiConfiguration }) => {
                const dialCountDownTime = uiConfiguration?.find(obj => obj.key === 'Dial.CountdownTime')?.value;
                this._state.update(state => ({ ...state, SubscriptionInterval: (parseInt(dialCountDownTime ?? '10') * 1000) + 5000 }));

                this.emailTopicSubscription = this.genesysCloudService.subscribeToTopic(emailTopic).subscribe(notification => {
                    if (notification?.eventBody?.participants?.some((participant: Models.EmailMediaParticipant) => participant.provider
                        && this.validProviders.includes(participant.provider))) {
                        this.handleAdmissionsEvents(notification.eventBody);
                    }
                });
            });
        }
    }

    private findLastInParticipants(participants: Models.EmailMediaParticipant[],
        predicate: (participant: any) => boolean): any {
        // Sort participants by id before searching
        const sortedParticipants = [...participants].sort((a, b) => (b?.startTime ?? '').localeCompare(a?.startTime ?? ''));
        return sortedParticipants.find(predicate);
    }

    private findLastInCallParticipants(participants: Models.CallMediaParticipant[],
        predicate: (participant: any) => boolean): any {
        // Sort participants by id before searching
        const sortedParticipants = [...participants].sort((a, b) => (b?.startTime ?? '').localeCompare(a?.startTime ?? ''));
        return sortedParticipants.find(predicate);
    }

    private extractCallTypeFromConversation(conversation: Models.EmailConversation): string {
        const customerParticipant = conversation.participants?.find((p: any) => p.purpose === 'customer');
        return customerParticipant?.attributes?.['LeadFlow_TelecomInteractionType'] ?? "callback";
    }

    async handleAdmissionsEvents(conversation: Models.EmailConversation) {
        if (this.isEmailDisconnected(conversation)) {
            this.endInteraction("", conversation.id);
            return;
        }

        if (conversation.id === this.lastProcessedConversationId) {
            this._state.update(state => ({ ...state, Email: conversation, CallType: 'callback' })); //UPdate the conversation as not all conversation having the user obj when status connected
            return;
        }
        this.clearState(); // We are clearing the state for the incoming email conversation
        const callType = this.extractCallTypeFromConversation(conversation);
        this._state.update(state => ({ ...state, Email: conversation, CallType: callType })); //update the state with the new conversation
        this.lastProcessedConversationId = conversation.id!; // Update the last processed conversation ID

        if (this.isEmailAlerting(conversation)) {
            this.updateCallStatus('alerting');                
            this.getEmailConversation(conversation.id!);                
        }
    }

    private getAgentParticipantState(conversation: Models.EmailConversation, state: string): boolean {
        const agentParticipant = this.findLastInParticipants(conversation?.participants!, (participant) =>
            (['agent', 'user'].includes(participant.purpose)) && participant?.user?.id !== undefined && participant?.user?.id === this.genesysUserId);
        return agentParticipant?.state === state;
    }
    isInAlertingState(): boolean {
        return this.commonDataService.alertingStatus.includes(this._state().CallStatus ?? '') || (this._state().Email ? this.isEmailAlerting(this._state().Email!) : false);
    }
    // Computed property for isAlerting
    isEmailAlerting(conversation: Models.EmailConversation): boolean {
        return this.getAgentParticipantState(conversation, 'alerting');
    }

    isEmailDisconnected(conversation: Models.EmailConversation) {
        return this.getAgentParticipantState(conversation, 'disconnected');
    }

    isEmailConnected(conversation: Models.EmailConversation) {
        return this.getAgentParticipantState(conversation, 'connected');
    }

    // Computed property for findAgentParticipant
    emailAgentParticipant = computed(() => {
        if (this._state().Email?.participants) {
            return this.findLastInParticipants(this._state().Email!.participants!, (participant) =>
                participant.user?.id !== undefined && participant.user.id === this.genesysUserId);
        }
        return undefined
    });

    // Computed property for getEmailConversation
    emailConversation = computed(() => {
        return this._state().Email;
    });

    emailStatus = computed(() => {
        return this.emailAgentParticipant()?.state;
    });

    getCallConversationObservable(callConversationId: string): Observable<Models.CallConversation> {
        return new Observable<Models.CallConversation>(obs => {
            this._state.update(state => ({ ...state, CallConversationId: callConversationId }));
            this.genesysCloudService.getCallConversationById(callConversationId)
                .subscribe({
                    next: (conversation: Models.CallConversation) => {
                        let associatedEmailConversationId: string | undefined;
                        if (conversation.id === this._state().CallConversationId) {
                            let agentParticipant = this.findLastInCallParticipants(conversation.participants!,
                                (participant: Models.CallMediaParticipant) => participant.user?.id !== undefined
                                    && participant.user.id === this.genesysUserId && (['agent', 'user'].includes(participant.purpose!))
                            );
                            associatedEmailConversationId = agentParticipant?.attributes?.['leadFlow_Associated_EmailConversationId'];
                            if(!associatedEmailConversationId || this._state().Email?.id == associatedEmailConversationId) {
                                this.updateCallConversation(conversation);
                                this.updateCallStatus(agentParticipant.state);
                                obs.next(conversation);
                            }else{
                                this.appInsightsService.logTrace('IgnoringCallConversationUpdate_inconsistentEmail', {
                                    conversationId: conversation.id,
                                    associatedEmailConversationId: associatedEmailConversationId,
                                    emailId: this._state().Email?.id
                                    });
                                console.warn(`Received conversation with id ${conversation.id} has associated email conversation id ${associatedEmailConversationId} which does not match the current email conversation id ${this._state().Email?.id}`);
                            }
                        }else{
                            console.warn(`Received conversation with id ${conversation.id} does not match the current conversation id ${this._state().CallConversationId}`);
                        }
                        obs.complete();
                    },
                    error: (error) => {
                        obs.error(error);
                        obs.complete();
                    }
                });
        });
    }

    getCallConversation(callConversationId: string): void {
        try {
            this.getCallConversationObservable(callConversationId).subscribe({
                error: (error) => {
                    console.error('LeadUI-An error occurred while fetching the call conversation:', error);
                }
            });
        } catch (error) {
            console.error('LeadUI-An error occurred while fetching the call conversation:', error);
        }
    }

    getEmailConversationObservable(emailConversationId: string): Observable<Models.EmailConversation> {
        return new Observable<Models.EmailConversation>(obs => {
            this.genesysCloudService.getEmailConversationById(emailConversationId).subscribe({
                next: (conversation) => {
                    //Gets the InteractionType from email participant attributes
                    const callType = this.extractCallTypeFromConversation(conversation);
                    this._state.update(state => ({ ...state, Email: conversation, CallType: callType }));
                    if (this.isEmailAlerting(conversation))
                        this.updateCallStatus('alerting');
                    obs.next(conversation);
                    obs.complete();
                },
                error: (error) => {
                    console.error('LeadUI-An error occurred while fetching the email conversation:', error);
                    obs.error(error);
                    obs.complete();
                }
            });
        });
    }

    getEmailConversation(emailConversationId: string): void {
        try {
            this.getEmailConversationObservable(emailConversationId).subscribe({
                error: (error) => {
                }
            });
        } catch (error) {
            console.error('LeadUI-An error occurred while fetching the email conversation:', error);
        }
    }

    updateCallConversation(conversation: Models.CallConversation): void {
        this._state.update(state => ({ ...state, Call: conversation }));
    }

    updatePstSMSDisabled(pstSMSDisabled: boolean): void {
        this._state.update(state => ({ ...state, PSTSMSDisabled: pstSMSDisabled }));
    }
    updatePstEmailDisabled(pstEmailDisabled: boolean): void {
        this._state.update(state => ({ ...state, PSTEmailDisabled: pstEmailDisabled }));
    }
    updatePreviousUserStatus(status: string): void {
        this._state.update(state => ({ ...state, PreviousUserStatus: status }));
    }

    updateCallStatus(status: string): void {
        const prev = this._state().CallStatus ?? '';
        const isDisconnectedToTerminated =
            prev === 'disconnected' &&
            this.commonDataService.terminatedStatus.includes(status);
        const isConnectedToConnectedOrTerminated =
            this.commonDataService.connectedStatus.includes(prev) &&
            (this.commonDataService.connectedStatus.includes(status) ||
                this.commonDataService.terminatedStatus.includes(status));
        const isAlerting = this.commonDataService.alertingStatus.includes(prev);
        // if there's no previous status, or any of the allowed transitions apply, update
        if (!prev ||
            isDisconnectedToTerminated ||
            isConnectedToConnectedOrTerminated ||
            isAlerting) {
            this._state.update(state => ({ ...state, CallStatus: status }));
        }

        if (isDisconnectedToTerminated && this.getState()().Student) {
            this.restoreLeadUi();
        }
    }


    setDispositionTimer(timestamp: number): void {
        this._state.update(state => ({ ...state, DispositionStartTimestamp: timestamp }));
    }

    getCallDuration(): number {
        if (!this._state().Call) return 0;
        let agentParticipant = this.findLastInParticipants(this._state().Call?.participants!, (participant) =>
            (participant.user?.id !== undefined && participant.user?.id === this.genesysUserId));

        if (!agentParticipant) return 0;
        if (!agentParticipant.connectedTime) return 0;
        const callStartTimestamp = new Date(agentParticipant.connectedTime).getTime();

        // If we have a disposition timestamp, use it for terminated calls
        if (this._state().DispositionStartTimestamp) {
            return Math.floor((this._state().DispositionStartTimestamp! - callStartTimestamp) / 1000);
        }
        // Otherwise, use current time
        const now = Date.now();
        return Math.floor((now - callStartTimestamp) / 1000);
    }

    getDispositionDuration(): number {
        const dispositionStartTimestamp = this._state().DispositionStartTimestamp;
        if (!dispositionStartTimestamp) return 0;

        const now = Date.now();
        return Math.floor((now - dispositionStartTimestamp) / 1000);
    }

    getState(): WritableSignal<LeadConversationState> {
        return this._state;
    }

    updatePhoneNumber(phoneNumber: string | undefined): void {
        this._state.update(state => ({ ...state, PhoneNumber: phoneNumber }));
    }

    updateCallType(callType: string): void {
        if (callType == null || callType.trim() === "") {
            const stackTrace = new Error().stack;
            this.appInsightsService.logEvent('InvalidCallType', { ...this.appInsightsProperties, CallType: callType, StackTrace: stackTrace});
        }
        this._state.update(state => ({ ...state, CallType: callType }));
    }
    updateDialerContactId(dialerContactId: string): void {
        this._state.update(state => ({ ...state, DialerContactId: dialerContactId }));
    }
    updateTelecomDialerCampaignName(telecomDialerCampaignName: string): void {
        this._state.update(state => ({ ...state, TelecomDialerCampaignName: telecomDialerCampaignName }));
    }
    updateLastActivePageName(lastActivePageName: string): void {
        this._state.update(state => ({ ...state, LastActivePageName: lastActivePageName }));
    }
    getStudent(studentId: number): Observable<Student | null> {
        return new Observable<Student | null>(obs => {
            this.leadService.apiLeadGetbySyStudentIdPost({ syStudentId: studentId })
                .subscribe({
                    next: (response) => {
                        this._state.update(state => ({ ...state, Student: response || {} }));
                        obs.next(response);
                        obs.complete();
                    },
                    error: (error) => {
                        obs.error(error);
                        obs.complete();
                    }
                });
        });
    }

    updateStudent(student: Student): void {
        this._state.update(state => ({ ...state, Student: student }));
    }

    declineMissedEmail(): Observable<void> {
        return this.genesysCloudService.declineEmailConversation(this._state().Email?.id!, this.emailAgentParticipant().id);
    }

    disconnectDispostionedEmail(): Observable<void> {
        return this.genesysCloudService.disconnectEmailConversation(this._state().Email?.id!);
    }

    // Add this method to make a request to localhost
    public restoreLeadUi() {
        const workerUrl = `${this.configurationService.workerConfig?.basePath}/restore/leadUi` || '';
        this.http.post(workerUrl, { WindowTitle: document.title }).subscribe({
            next: (response) => {
                // Removed unnecessary console.log
            },
            error: (err) => {
                console.error('Error calling localhost:', err);
            }
        });
    }

    handleUserPresenceStatus(presence: Models.PresenceEventUserPresence): void {
        if(['AWAY','BREAK','MEAL','MEETING','TRAINING'].includes(presence.presenceDefinition?.systemPresence || ''))
        {
            this._state.update(state => ({ ...state, PreviousUserStatus: this.toCamelCase(presence.presenceDefinition?.systemPresence || '') }));
        }
    }

    toCamelCase(presence: string): string {
        return presence.charAt(0).toUpperCase() + presence.slice(1).toLowerCase();
    }
}
